import React, { useState } from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { usePlaytesterStore } from '../../stores/playtester-store.js';
import { getCardImageUri } from '@mtg-deck-tool/shared';
import type { CardInstance } from '@mtg-deck-tool/shared';
import { HordeBattlefield } from './HordeBattlefield.js';
import { HordeRevealPanel } from './HordeRevealPanel.js';
import { CARD_CORNER_RADIUS } from '../../lib/playtester-engine.js';

function HordeLibraryBadge() {
  const count = usePlaytesterStore((s) => s.horde?.library.length ?? 0);
  const { setNodeRef, isOver } = useDroppable({ id: 'horde-library' });

  return (
    <div
      ref={setNodeRef}
      className={[
        'flex items-center gap-2 px-3 py-1.5 rounded border cursor-default select-none',
        isOver ? 'border-orange-400 bg-orange-950/40' : 'border-gray-600 bg-gray-800',
      ].join(' ')}
      title="Horde Library"
    >
      <span className="text-gray-400 text-xs">Library</span>
      <span className="text-white text-sm font-mono font-bold">{count}</span>
    </div>
  );
}

function HordeMill() {
  const millInput = usePlaytesterStore((s) => s.horde?.millInput ?? 5);
  const setHordeMillInput = usePlaytesterStore((s) => s.setHordeMillInput);
  const hordeMillCards = usePlaytesterStore((s) => s.hordeMillCards);

  return (
    <div className="flex items-center gap-1">
      <span className="text-gray-400 text-xs">Mill</span>
      <input
        type="number"
        value={millInput}
        min={1}
        max={99}
        onChange={(e) => setHordeMillInput(Number(e.target.value))}
        className="w-12 bg-gray-700 border border-gray-600 rounded px-1.5 py-0.5 text-white text-xs text-center"
      />
      <button
        onClick={() => hordeMillCards(millInput)}
        className="px-2 py-0.5 rounded bg-gray-700 hover:bg-gray-600 text-white text-xs"
      >
        Mill
      </button>
    </div>
  );
}

function HordeGraveyardRow({ instance }: { instance: CardInstance }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `horde-graveyard-${instance.instanceId}`,
    data: { instanceId: instance.instanceId, fromHordeBattlefield: false, fromHordeGraveyard: true },
  });
  const artUri = getCardImageUri(instance.card, 'artCrop');

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-800 cursor-grab active:cursor-grabbing transition ${
        isDragging ? 'opacity-40' : ''
      }`}
    >
      {artUri && (
        <img src={artUri} alt={instance.card.name} className="w-8 h-8 rounded object-cover shrink-0 border border-gray-700" />
      )}
      <div className="min-w-0">
        <p className="text-white text-sm truncate">{instance.card.name}</p>
        <p className="text-gray-500 text-xs truncate">{instance.card.typeLine}</p>
      </div>
    </div>
  );
}

function HordeGraveyardPanel({ onClose }: { onClose: () => void }) {
  const graveyard = usePlaytesterStore((s) => s.horde?.graveyard ?? []);
  const [filter, setFilter] = useState('');
  const [hovered, setHovered] = useState<CardInstance | null>(null);
  const filtered = filter
    ? graveyard.filter((i) => i.card.name.toLowerCase().includes(filter.toLowerCase()))
    : graveyard;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60">
      <div className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-md mx-4 flex gap-3 max-h-[70vh]">
        {/* Hover preview pane */}
        {hovered && (
          <div className="shrink-0 p-3 flex items-start">
            <img
              src={getCardImageUri(hovered.card, 'large') ?? ''}
              alt={hovered.card.name}
              className="rounded-lg shadow-2xl border border-gray-700"
              style={{ borderRadius: CARD_CORNER_RADIUS,  width: 160, height: 224, objectFit: 'cover' }}
            />
          </div>
        )}
        {/* List pane */}
        <div className="flex flex-col gap-3 p-4 flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center justify-between shrink-0">
            <h3 className="text-white font-semibold">Horde Graveyard ({graveyard.length})</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white text-lg font-bold">✕</button>
          </div>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search..."
            className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-white text-sm shrink-0"
          />
          <div className="overflow-y-auto flex flex-col gap-1">
            {filtered.length === 0 && (
              <p className="text-gray-500 text-sm italic text-center py-4">Empty</p>
            )}
            {filtered.map((instance) => (
              <div
                key={instance.instanceId}
                onMouseEnter={() => setHovered(instance)}
                onMouseLeave={() => setHovered(null)}
              >
                <HordeGraveyardRow instance={instance} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function HordeGraveyardBadge({ onOpen }: { onOpen: () => void }) {
  const count = usePlaytesterStore((s) => s.horde?.graveyard.length ?? 0);
  const { setNodeRef, isOver } = useDroppable({ id: 'horde-graveyard' });

  return (
    <button
      ref={setNodeRef}
      onClick={onOpen}
      className={[
        'flex items-center gap-2 px-3 py-1.5 rounded border',
        isOver ? 'border-orange-400 bg-orange-950/40' : 'border-gray-600 bg-gray-800 hover:border-gray-500',
      ].join(' ')}
      title="Horde Graveyard"
    >
      <span className="text-gray-400 text-xs">Graveyard</span>
      <span className="text-white text-sm font-mono font-bold">{count}</span>
    </button>
  );
}

export function HordeArea() {
  const horde = usePlaytesterStore((s) => s.horde);
  const [showGraveyard, setShowGraveyard] = useState(false);

  if (!horde) return null;

  return (
    <div className="flex flex-col gap-2 border border-gray-700 rounded-lg bg-gray-900/50 p-3">
      {/* Header row */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-gray-300 text-sm font-semibold">{horde.deckName}</span>
        <HordeLibraryBadge />
        <HordeGraveyardBadge onOpen={() => setShowGraveyard(true)} />
        <HordeMill />

        {/* Passive reminder */}
        <span className="ml-auto text-gray-500 text-xs italic">
          All horde creatures have Haste and attack each turn.
        </span>
      </div>

      {/* Horde Battlefield */}
      <HordeBattlefield />

      {/* Reveal Panel (shown during horde-resolving phase) */}
      {horde.phase === 'horde-resolving' && <HordeRevealPanel />}

      {/* Horde Graveyard panel */}
      {showGraveyard && (
        <HordeGraveyardPanel onClose={() => setShowGraveyard(false)} />
      )}
    </div>
  );
}
